import React, { useContext, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, Flex, Icon, InterfaceErrorIcon, Text } from '@grammarly/design-system'
import { FormattedMessage, useIntl } from 'react-intl'

import { gnarSpec } from '@lib/tracking/gnar'
import { PageContext } from '@contexts/page-context'
import { UploadButton } from './upload-button'
import { analyzeTextAndMaybeTruncateToDesiredWordLimit } from '../utils'
import { useTool } from '../tool-context'
import { ToolFormValues } from '../types'
import { maximumCharacterLimit, ToolErrors } from '../constants'

import styles from '../tool.module.scss'

export const ToolActionRow: React.FC = () => {
  const { toolInputName, isLoading, submitButtonText, canUploadFile, setToolError, sampleText, toolType } = useTool()
  const intl = useIntl()

  const { pageId, slug } = useContext(PageContext)

  const { watch, setValue, clearErrors } = useFormContext<ToolFormValues>()
  const text = watch(toolInputName)

  const wordCount = analyzeTextAndMaybeTruncateToDesiredWordLimit(text).words

  return (
    <Flex
      direction="row"
      marginTop="auto"
      paddingTop={5}
      paddingBottom={5}
      paddingLeft={4}
      paddingRight={4}
      align="center"
      justify="space-between"
      gap={2}
      wrap
    >
      <Flex direction="row" align="center" gap={2}>
        <Button type="submit" isLoading={isLoading} isDisabled={isLoading}>
          <span style={{ opacity: isLoading ? 0 : 1 }}>{submitButtonText}</span>
        </Button>
        {canUploadFile && (
          <UploadButton
            onError={(error, _fileName, maxFileSize, userFileSize) => {
              switch (error) {
                case 'FILE_ERROR_INVALID_EXT':
                  setToolError(
                    intl.formatMessage({
                      id: 'tool.input.toolActionRow.error.fileErrorInvalidExtension',
                      description: 'Tool - Input - Tool Action Row - Error - File Error Invalid Extension',
                      defaultMessage: 'File type not supported. Please upload a .doc, .docx, or .txt.',
                    })
                  )
                  break
                case 'FILE_ERROR_TOO_BIG': {
                  if (typeof maxFileSize === 'number' && typeof userFileSize === 'number') {
                    const maxFileSizeMB = (maxFileSize / 1000000).toFixed(2)
                    const userFileSizeMB = (userFileSize / 1000000).toFixed(2)
                    setToolError(
                      intl.formatMessage(
                        {
                          id: 'tool.input.toolActionRow.error.fileErrorTooBig.withSize',
                          description: 'Tool - Input - Tool Action Row - Error - File Error Too Big - With Size',
                          defaultMessage:
                            'The file size of {userFileSizeMB} MB exceeds the {maxFileSizeMB} MB limit. Please upload a smaller file.',
                        },
                        { userFileSizeMB, maxFileSizeMB }
                      )
                    )
                  } else {
                    setToolError(
                      intl.formatMessage({
                        id: 'tool.input.toolActionRow.error.fileErrorTooBig.generic',
                        description: 'Tool - Input - Tool Action Row - Error - File Error Too Big - Generic',
                        defaultMessage: 'The file uploaded is too large. Please upload a smaller file.',
                      })
                    )
                  }
                  break
                }
                case 'UNKNOWN_ERROR':
                default:
                  setToolError(
                    intl.formatMessage({
                      id: 'tool.input.toolActionRow.error.unknownError',
                      description: 'Tool - Input - Tool Action Row - Error - Unknown Error',
                      defaultMessage: 'There was an error uploading your file.',
                    })
                  )
                  break
              }

              console.error(error)
              gnarSpec.toolFileUploadFailure(error, pageId, slug, userFileSize)
            }}
            text={intl.formatMessage({
              id: 'global.uiAction.uploadFile',
              description: 'Text for CTA that prompts user to upload a file',
              defaultMessage: 'Upload file',
            })}
            isDisabled={isLoading}
            isLoading={isLoading}
            onFileChangeFinish={(text, fileInfo) => {
              setValue(toolInputName, text.slice(0, maximumCharacterLimit(toolType)))
              gnarSpec.toolFileUploadSuccess(pageId, slug, fileInfo.size, fileInfo.type)
            }}
            onClick={() => {
              setToolError(null)
              clearErrors(toolInputName)
              gnarSpec.toolFileUploadButtonClick(pageId, slug)
            }}
          />
        )}
        {sampleText && (
          <Button
            variant="tertiary"
            isDisabled={isLoading}
            onClick={() => {
              setValue(toolInputName, sampleText)
              gnarSpec.toolSampleTextClick(pageId, slug)
            }}
          >
            <FormattedMessage
              id="tool.input.toolActionRow.ctaText"
              description="Tool - Input - Tool Action Row - CTA Text"
              defaultMessage="Try sample text"
            />
          </Button>
        )}
      </Flex>
      <WordCounter wordCount={wordCount} />
    </Flex>
  )
}

interface WordCounterProps {
  wordCount: number
}

const WordCounter: React.FC<WordCounterProps> = ({ wordCount }) => {
  const { toolInputName, minWordLimit, maxWordLimit } = useTool()
  const { subscribe } = useFormContext<ToolFormValues>()
  const [inputError, setInputError] = useState<React.ReactNode | null>(null)

  useEffect(() => {
    const errorSubscription = subscribe({
      formState: {
        errors: true,
      },
      callback: ({ errors }) => {
        if (errors) {
          const errorMessage = errors[toolInputName]?.message
          if (errorMessage) {
            setInputError(errorMessage)
          } else {
            setInputError(null)
          }
        }
      },
    })
    return () => errorSubscription()
  }, [minWordLimit, subscribe, toolInputName, wordCount])

  if (inputError) {
    const errorMessage =
      inputError === ToolErrors.MIN_WORD_LIMIT_ERROR ? (
        <FormattedMessage
          id="tool.input.toolActionRow.wordCounter.error.minWordLimitError"
          description="Tool - Input - Tool Action Row - Word Counter - Error - Min Word Limit Error"
          defaultMessage="Enter {wordsLeft} more {wordsLeft, plural, one {word} other {words}}"
          values={{ wordsLeft: minWordLimit - wordCount }}
        />
      ) : (
        <FormattedMessage
          id="tool.input.toolActionRow.wordCounter.error.generic"
          description="Tool - Input - Tool Action Row - Word Counter - Error - Generic"
          defaultMessage="Please enter text"
        />
      )

    return (
      <Flex direction="row" align="center" justify="center" gap={1}>
        <Icon icon={InterfaceErrorIcon} accessibilityLabel="" />
        <Text variant="text-xsmall" as="span" color="critical-default" className={styles.toolActionRowErrorCopy}>
          {errorMessage}
        </Text>
      </Flex>
    )
  }

  if (wordCount < minWordLimit) {
    const wordsLeft = minWordLimit - wordCount

    return (
      <Flex direction="row" align="center" gap={1}>
        <Text variant="text-xsmall" as="span">
          <FormattedMessage
            id="tool.input.toolActionRow.wordCounter.underMinWordLimit"
            description="Tool - Input - Tool Action Row - Word Counter - Under Minimum Word Limit"
            defaultMessage="Enter {wordsLeft} more {wordsLeft, plural, one {word} other {words}}"
            values={{ wordsLeft }}
          />
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="row" align="center" gap={1}>
      <Text variant="text-xsmall" as="span">
        <FormattedMessage
          id="tool.input.toolActionRow.wordCounter.wordCount"
          description="Tool - Input - Tool Action Row - Word Counter - Word Count"
          defaultMessage="{wordCount}/{maxWordLimit} words"
          values={{ wordCount, maxWordLimit }}
        />
      </Text>
    </Flex>
  )
}
